import React, { useCallback, useEffect, useRef, useState } from 'react';
import { requestJson } from '../utils/authClient.js';
import { useTranslation } from '../hooks/useTranslation.js';
import { AdminDialog, AdminSection, ShowMore, useShowMore } from '../components/AdminNav.jsx';
import { displayModelName, formatBerlin, formatUsd } from './VeridionShared.jsx';

// The organisation of the company in Webex (organisation/README.md) as Veridion Core runs it: the
// organisation chart, the positions with their bots, token usage, descriptions, interest profiles and
// speaking thresholds, the human decision makers, the reading authorisation of the company's
// spaces, the open approvals and questions with the space they came from and the schedules people
// asked for. Every name, title, area, agent and space comes from the runtime that owns it: the
// positions, areas, decision makers and spaces from Veridion Core, the agent definitions and the
// hosted agents from Veridion Core, the bots' names in Webex and the connected systems from the
// company instance. Only the labels of the surface itself are translated.
// The organisation is a group of pages of the dashboard (IDD D-HP-ADMIN-040): the organisation chart, the AI
// colleagues with their mailboxes, the voices, the people and the spaces, the approvals and schedules, and the editing
// of the organisation, each under its own address (`#organisation/<page>`). Writing to a colleague and calling one open
// a dialog over the page (IDD D-HP-ADMIN-041), so the page never jumps.
const CORE = '/api/admin/veridion-core';
const SUB_PAGES = ['chart', 'colleagues', 'voices', 'people', 'approvals', 'edit'];
const BRIDGE = '/api/admin/veridion-paioneer/webex';
const REFRESH_MS = 15000;

const call = (base, path, body) => requestJson(`${base}${path}`, body === undefined ? {} : { method: 'POST', body: JSON.stringify(body) });
const formatInteger = (value) => (value === null || value === undefined || value === '' ? '-' : Number(value).toLocaleString());
const formatTime = formatBerlin;
// The first sentence of a position's description, as long as Veridion Core gives no shorter line of its responsibility.
const firstSentence = (text) => {
  const value = String(text || '').trim();
  const match = value.match(/^[\s\S]*?[.!?](?=\s|$)/);
  return match ? match[0] : value;
};

// A section of a sub-page: the shared section with its state in the header (IDD D-HP-ADMIN-040).
const Section = ({ id = '', title, subtitle, children, className = '', state = '', count = null, tone = '', collapsed = false }) => (
  <AdminSection id={id} title={title} subtitle={subtitle} className={className} state={state} count={count} tone={tone} collapsed={collapsed}>
    {children}
  </AdminSection>
);

// -- write and call: two quiet icons on every colleague's box (IDD D-HP-ADMIN-028) ---------------------

const PenIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3.5 16.5h3l9-9-3-3-9 9v3z" />
    <path d="M11 6l3 3" />
  </svg>
);

const HandsetIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.2 3.2h3l1.5 3.6-1.9 1.4a10 10 0 0 0 4.9 4.9l1.4-1.9 3.6 1.5v3a1.5 1.5 0 0 1-1.6 1.5A13.8 13.8 0 0 1 2.7 4.8a1.5 1.5 0 0 1 1.5-1.6z" />
  </svg>
);

// -- the call: a phone (IDD D-HP-ADMIN-037, D-HP-ADMIN-038, D-VCORE-253 to D-VCORE-256) ----------------------------

const CALL_RATE = 16000;
const CALL_FRAME = 320; // 20 ms at 16 kHz
const RINGING = 'ringing';
const CONNECTED = 'connected';
const ENDED = 'ended';

// The person's audio as the Core hears it: 16 kHz, one channel, 16 bit, in frames of 20 ms. The browser's context runs
// at its own rate; the samples are averaged down to 16 kHz and cut into frames, the remainder waits for the next buffer.
const makeDownsampler = (sampleRate) => {
  const ratio = sampleRate / CALL_RATE;
  let rest = new Int16Array(0);
  return (samples) => {
    const count = Math.floor(samples.length / ratio);
    const pcm = new Int16Array(rest.length + count);
    pcm.set(rest, 0);
    let peak = 0;
    for (let i = 0; i < count; i += 1) {
      const from = Math.floor(i * ratio);
      const to = Math.min(samples.length, Math.floor((i + 1) * ratio));
      let sum = 0;
      for (let j = from; j < to; j += 1) sum += samples[j];
      const value = Math.max(-1, Math.min(1, to > from ? sum / (to - from) : 0));
      peak = Math.max(peak, Math.abs(value));
      pcm[rest.length + i] = value < 0 ? value * 0x8000 : value * 0x7fff;
    }
    const frames = Math.floor(pcm.length / CALL_FRAME);
    const out = pcm.subarray(0, frames * CALL_FRAME);
    rest = pcm.slice(frames * CALL_FRAME);
    return { bytes: out.length ? new Uint8Array(out.buffer, out.byteOffset, out.length * 2) : null, peak };
  };
};

const formatDuration = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

// The telemetry of one spoken turn as the Core books it (IDD D-VCORE-267): the model, the tokens, the cost and the
// milliseconds of the ear, the mind and the voice; shown under the answer it belongs to.
const telemetryText = (o, row) => String(o.telemetryLine || '')
  .replace('{model}', displayModelName(row.model) || '-')
  .replace('{prompt}', formatInteger(row.prompt_tokens || 0))
  .replace('{completion}', formatInteger(row.completion_tokens || 0))
  .replace('{cost}', formatUsd(Number(row.cost || 0)))
  .replace('{asr}', formatInteger(row.asr_ms || 0))
  .replace('{llm}', formatInteger(row.llm_ms || 0))
  .replace('{tts}', formatInteger(row.tts_ms || 0))
  .replace('{total}', formatInteger(row.total_ms || 0))
  .replace('{deferred}', Number(row.deferred || 0) ? String(o.telemetryDeferred || '').replace('{count}', String(row.deferred)) : '');

const CallPanel = ({ o, position, language, onClose }) => {
  const [phase, setPhase] = useState(RINGING);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [level, setLevel] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const [lines, setLines] = useState([]);
  const socket = useRef(null);
  const stream = useRef(null);
  const context = useRef(null);
  const playAt = useRef(0);
  const sources = useRef([]);
  const alive = useRef(true);
  const started = useRef(0);
  const tallies = useRef({});

  const addLine = (who, text, key) => setLines((known) => {
    if (key !== undefined) {
      const at = known.findIndex((entry) => entry.key === key);
      if (at >= 0) return known.map((entry, index) => (index === at ? { ...entry, text } : entry));
    }
    return [...known, { key: key ?? `${Date.now()}-${known.length}`, who, text }];
  });

  const stopPlayback = () => {
    sources.current.forEach((source) => { try { source.stop(); } catch { /* already ended */ } });
    sources.current = [];
    playAt.current = context.current ? context.current.currentTime : 0;
    setSpeaking(false);
  };

  const playChunk = async (data) => {
    const audio = context.current;
    if (!audio) return;
    let buffer;
    try {
      buffer = await audio.decodeAudioData(data.slice(0));
    } catch {
      return;
    }
    const source = audio.createBufferSource();
    source.buffer = buffer;
    source.connect(audio.destination);
    const at = Math.max(audio.currentTime, playAt.current);
    source.start(at);
    playAt.current = at + buffer.duration;
    sources.current.push(source);
    setSpeaking(true);
    source.onended = () => {
      sources.current = sources.current.filter((entry) => entry !== source);
      if (!sources.current.length && alive.current) setSpeaking(false);
    };
  };

  const hangUp = useCallback((why) => {
    const ws = socket.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: 'hangup' })); } catch { /* closing anyway */ }
    }
    if (ws) { socket.current = null; try { ws.close(); } catch { /* already closed */ } }
    if (stream.current) { stream.current.getTracks().forEach((track) => track.stop()); stream.current = null; }
    stopPlayback();
    if (context.current) { context.current.close().catch(() => {}); context.current = null; }
    if (alive.current) { setPhase(ENDED); if (why) setReason(why); }
  }, []);

  useEffect(() => {
    alive.current = true;
    let timer = null;
    (async () => {
      let ticket;
      try {
        ticket = await call(CORE, '/call-ticket', { position: position.key, language });
      } catch (failure) {
        setError(`${o.callFailed} (${failure?.code || failure?.status || '-'})`);
        setPhase(ENDED);
        return;
      }
      let media;
      try {
        media = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      } catch {
        setError(o.callMicDenied);
        setPhase(ENDED);
        return;
      }
      if (!alive.current) { media.getTracks().forEach((track) => track.stop()); return; }
      stream.current = media;
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const audio = new AudioContextClass();
      context.current = audio;
      // The click on the handset is the page's user activation; the context may start suspended and is resumed on it.
      audio.resume().catch(() => {});
      const downsample = makeDownsampler(audio.sampleRate);
      const source = audio.createMediaStreamSource(media);
      const processor = audio.createScriptProcessor(2048, 1, 1);
      processor.onaudioprocess = (event) => {
        const ws = socket.current;
        const { bytes, peak } = downsample(event.inputBuffer.getChannelData(0));
        const now = Date.now();
        if (now - (tallies.current.level || 0) > 100) { tallies.current.level = now; setLevel(Math.min(100, Math.round(peak * 140))); }
        if (bytes && ws && ws.readyState === WebSocket.OPEN) ws.send(bytes);
      };
      source.connect(processor);
      processor.connect(audio.destination);
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${scheme}://${window.location.host}${ticket.path}`);
      ws.binaryType = 'arraybuffer';
      socket.current = ws;
      ws.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer) { playChunk(event.data); return; }
        let message;
        try { message = JSON.parse(event.data); } catch { return; }
        switch (message.type) {
          case 'connected':
            started.current = Date.now();
            setPhase(CONNECTED);
            addLine('bot', message.greeting);
            timer = window.setInterval(() => setSeconds(Math.floor((Date.now() - started.current) / 1000)), 1000);
            break;
          case 'transcript':
            addLine('person', message.text, `u${message.seq}`);
            break;
          case 'heard':
            if (!message.text) setError(message.error ? `${o.callFailed} (${message.error})` : o.callNothingHeard);
            break;
          case 'answer':
            if (message.pending) { addLine('bot', o.callThinking, `a${message.seq}`); break; }
            addLine('bot', message.superseded ? `${message.text} (${o.callOvertaken})` : message.text, `a${message.seq}`);
            break;
          case 'telemetry':
            addLine('meta', telemetryText(o, message), `t${message.seq}`);
            break;
          case 'stop':
            stopPlayback();
            break;
          case 'voice_unavailable':
            setError(o.callNoVoice);
            break;
          case 'error':
            setError(`${o.callFailed} (${message.code || '-'})`);
            break;
          case 'ended':
            setReason(message.reason || 'closed');
            setSeconds(message.seconds || 0);
            hangUp('');
            break;
          default:
            break;
        }
      };
      ws.onerror = () => { if (alive.current && phase !== ENDED) setError(o.callFailed); };
      ws.onclose = (event) => {
        if (!alive.current) return;
        if (event.code === 4401) setError(o.callRefused);
        hangUp(reason || 'closed');
      };
    })();
    return () => { alive.current = false; if (timer) window.clearInterval(timer); hangUp(''); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position.key, language]);

  const title = String(o.callTitle || '').replace('{bot}', position.bot);
  const reasonText = { hangup: o.callEndedHangup, closed: o.callEndedClosed, silence: o.callEndedSilence, limit: o.callEndedLimit, error: o.callEndedError }[reason] || '';
  // The transcript keeps its newest line in view inside the dialog; the page behind does not move.
  const turns = useRef(null);
  useEffect(() => { if (turns.current) turns.current.scrollTop = turns.current.scrollHeight; }, [lines]);
  // Escape and the backdrop never drop a running call: they ask once, then hang up; an ended call closes at once.
  const requestClose = () => {
    if (phase === ENDED) { onClose(); return; }
    if (window.confirm(o.callConfirmEnd)) hangUp('hangup');
  };
  return (
    <AdminDialog title={title} subtitle={position.title} onRequestClose={requestClose} className="org-call-dialog" labels={{ close: o.close }}>
      <div className="org-call__status">
        <span className={`org-call__state org-call__state--${phase}`}>
          {phase === RINGING ? o.callRinging : phase === CONNECTED ? `${o.callConnected} ${formatDuration(seconds)}` : `${o.callEnded} ${formatDuration(seconds)}${reasonText ? ` · ${reasonText}` : ''}`}
        </span>
        {phase === CONNECTED ? (
          <span className="org-call__meter" title={o.callLevel} aria-label={o.callLevel}>
            <span className="org-call__meter-bar" style={{ width: `${level}%` }} />
          </span>
        ) : null}
        {phase === CONNECTED && speaking ? <span className="text-xs text-text-secondary">{o.callSpeaking}</span> : null}
      </div>
      {error ? <p className="text-sm text-error">{error}</p> : null}
      <div ref={turns} className="org-dialog__body org-call__turns">
        {lines.map((line) => (
          <div key={line.key} className={`org-dialog__turn ${line.who === 'person' ? 'org-dialog__turn--person' : line.who === 'meta' ? 'org-dialog__turn--meta' : 'org-dialog__turn--bot'}`}>
            {line.who !== 'meta' ? <div className="org-dialog__who">{line.who === 'person' ? o.callYou : position.bot}</div> : null}
            {line.text}
          </div>
        ))}
        {!lines.length ? <div className="text-sm text-text-secondary">{o.callSubtitle}</div> : null}
      </div>
      <div className="org-dialog__foot">
        {phase === ENDED
          ? <button type="button" className="homepage-button-secondary" onClick={onClose}>{o.close}</button>
          : <button type="button" className="homepage-button-primary org-call__hangup" onClick={() => hangUp('hangup')}>{o.callHangUp}</button>}
      </div>
    </AdminDialog>
  );
};

// -- writing to a colleague: the same dialog family (IDD D-HP-ADMIN-041) ------------------------------------------

// The written conversation with an AI colleague from the dashboard (IDD D-VCORE-116): read and written through the
// colleague's message door; every message and its answer are also said in Webex, in the administrator's direct room
// with the bot. The thread stays when the dialog closes; it is a mailbox, not a call.
const WRITE_IDLE_MS = 10000;
const WRITE_PENDING_MS = 3000;

const WriteDialog = ({ o, c, position, onClose }) => {
  const [thread, setThread] = useState([]);
  const [text, setText] = useState('');
  const [state, setState] = useState({ busy: false, message: '', error: false });
  const body = useRef(null);
  const door = `/organisation/positions/${encodeURIComponent(position.key)}/messages`;
  const pending = thread.some((entry) => !entry.answered_at);
  const load = useCallback(async () => {
    try {
      setThread((await call(CORE, door)).items || []);
    } catch {
      // The thread keeps what it showed.
    }
  }, [door]);
  useEffect(() => { load(); const timer = setInterval(load, pending ? WRITE_PENDING_MS : WRITE_IDLE_MS); return () => clearInterval(timer); }, [load, pending]);
  useEffect(() => { if (body.current) body.current.scrollTop = body.current.scrollHeight; }, [thread]);
  const send = async (event) => {
    event.preventDefault();
    setState({ busy: true, message: '', error: false });
    try {
      await call(CORE, door, { text });
      setText('');
      setState({ busy: false, message: c.messageSent, error: false });
      load();
    } catch (failure) {
      const code = failure?.code || failure?.status || '-';
      setState({ busy: false, message: `${c.errors?.[code] || c.failed} (${code})`, error: true });
    }
  };
  return (
    <AdminDialog title={String(o.writeTitle || '').replace('{bot}', position.bot)} subtitle={position.title} onRequestClose={onClose} className="org-write-dialog" labels={{ close: o.close }}>
      <div ref={body} className="org-dialog__body">
        {thread.length ? thread.map((entry) => (
          <div key={entry.id} className="space-y-1">
            {entry.text ? (
              <div className="org-dialog__turn org-dialog__turn--person">
                <div className="org-dialog__who">{formatTime(entry.created_at)} · {entry.sender || o.callYou}</div>
                {entry.text}
              </div>
            ) : null}
            <div className="org-dialog__turn org-dialog__turn--bot">
              <div className="org-dialog__who">{entry.answered_at ? `${formatTime(entry.answered_at)} · ` : ''}{position.bot}</div>
              {entry.answer || <em>{c.answerPending}</em>}
            </div>
          </div>
        )) : <div className="text-sm text-text-secondary">{c.conversationEmpty}</div>}
      </div>
      <form className="org-dialog__foot org-dialog__form" onSubmit={send}>
        <textarea className="form-input w-full" rows={3} value={text} onChange={(event) => setText(event.target.value)} placeholder={c.writeToAgent} required />
        <div className="org-dialog__form-row">
          <span className={`text-xs ${state.error ? 'text-error' : 'text-text-secondary'}`}>{state.message || c.conversationHint}</span>
          <button type="submit" className="homepage-button-primary" disabled={state.busy}>{c.send}</button>
        </div>
      </form>
    </AdminDialog>
  );
};

// -- the organisation chart ----------------------------------------------------------------------------

// One AI colleague as a node: the name of its Webex bot exactly as Webex reports it (Veridion Core's name
// of the bot only while Webex reports none), the job it holds and its responsibility as Veridion Core
// names them, its state and its tokens of the last 30 days. "Write" opens its conversation: a message to
// the AI colleague from the dashboard, answered in the dashboard and in Webex (IDD D-VCORE-116).
// The voices a bot may speak with (IDD D-VCORE-288): the audio model's speakers (`openrouter:<speaker>`) and the company's
// own voices (the cloned clips of the voice store, or the engine's presets), grouped; the default is marked.
const CLOUD_VOICE = 'openrouter:';
const voiceLabel = (o, name, position) => `${name.startsWith(CLOUD_VOICE) ? name.slice(CLOUD_VOICE.length) : name}${name === position.defaultVoice ? ` (${o.voiceDefault})` : ''}`;
const VoiceOptions = ({ o, voices, position, current }) => {
  const cloud = voices.filter((name) => name.startsWith(CLOUD_VOICE));
  const own = voices.filter((name) => !name.startsWith(CLOUD_VOICE));
  return (
    <>
      {!current || !voices.includes(current) ? <option value="">{o.voiceNone}</option> : null}
      {cloud.length ? <optgroup label={o.voiceCloud}>{cloud.map((name) => <option key={name} value={name}>{voiceLabel(o, name, position)}</option>)}</optgroup> : null}
      {own.length ? <optgroup label={o.voiceOwn}>{own.map((name) => <option key={name} value={name}>{voiceLabel(o, name, position)}</option>)}</optgroup> : null}
    </>
  );
};

const PositionNode = ({ position, o, statusOf, webexName, onWrite, onCall, voices = [], onVoice = null, onStanding = null, compact = false }) => {
  const responsibility = position.responsibility || firstSentence(position.description);
  const running = position.status === 'running';
  const standby = position.standing === 'standby';
  // The node tells at a glance where the colleague stands (IDD D-HP-ADMIN-042): its unit's thread prefix, whether it
  // leads its unit, a runtime of its own and a cloud model; the description stays in the title attribute and on the
  // colleagues page, so the chart stays readable.
  const badges = [
    position.prefix ? ['prefix', position.prefix] : null,
    position.lead ? ['lead', o.unitLead] : null,
    position.runtime && position.runtime !== 'coding' ? ['runtime', `${o.runtime}: ${position.runtime}`] : null,
    position.modelSource === 'cloud' && position.model ? ['model', `${o.modelCloud}: ${displayModelName(position.model)}`] : null,
  ].filter(Boolean);
  if (compact) {
    return (
      <div className={`org-node org-node--support${standby ? ' org-node--standby' : ''}`} title={responsibility || ''}>
        <div className="org-node__name">{webexName || position.bot}</div>
        <div className="org-node__title">{position.title}{position.unitName ? ` · ${position.unitName}` : ''}</div>
      </div>
    );
  }
  return (
    <div className={`org-node${standby ? ' org-node--standby' : ''}`} title={responsibility || ''}>
      <div className="org-node__name">{webexName || position.bot}</div>
      <div className="org-node__title">{position.title}</div>
      {badges.length ? <div className="org-node__badges">{badges.map(([kind, text]) => <span key={kind} className={`org-badge org-badge--${kind}`}>{text}</span>)}</div> : null}
      <div className="org-node__meta">
        <span className={running && !standby ? '' : 'org-node__marker'} title={position.activity || ''}>
          {statusOf(position.status)}{position.standing ? ` · ${standby ? o.standingStandby : (position.activity || o.standingActive)}` : ''}
        </span>
        <span>{o.tokens30}: {formatInteger(position.tokens30Days)}</span>
        {Number(position.callTurns30Days) ? <span>{o.calls30}: {formatInteger(position.callTurns30Days)}{Number(position.callCost30Days) ? ` · ${formatUsd(position.callCost30Days)}` : ''}</span> : null}
      </div>
      {onWrite || onCall || onVoice || onStanding ? (
        <div className="org-node__actions">
          {onWrite ? <button type="button" className="org-node__icon" title={o.write} aria-label={o.write} onClick={() => onWrite(position.agentId)}><PenIcon /><span className="org-node__icon-label">{o.write}</span></button> : null}
          {onCall ? <button type="button" className="org-node__icon" title={o.call} aria-label={o.call} onClick={() => onCall(position.key)}><HandsetIcon /><span className="org-node__icon-label">{o.call}</span></button> : null}
          {onStanding && position.standing ? (
            <button type="button" className="org-node__icon org-node__switch" title={o.standbyHint} aria-label={standby ? o.standbyOff : o.standbyOn}
              onClick={() => window.confirm(String(standby ? o.confirmActivate : o.confirmStandby).replace('{name}', webexName || position.bot)) && onStanding(position.key, standby ? 'active' : 'standby')}>
              <span className="org-node__icon-label">{standby ? o.standbyOff : o.standbyOn}</span>
            </button>
          ) : null}
          {onVoice ? (
            <select className="org-node__voice" title={o.voice} aria-label={o.voice} value={position.voice || ''}
              onChange={(event) => onVoice(position.key, event.target.value)}>
              <VoiceOptions o={o} voices={voices} position={position} current={position.voice} />
            </select>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const OrganisationChart = ({ o, t, organisation, hosted, definitions, webexBots, servers, spaces, botOf, statusOf, onWrite, onCall, voices = [], onVoice = null, onStanding = null, onUnitStatus = null, onUnitAutopilot = null }) => {
  const areas = organisation.areas || {};
  const positions = organisation.positions || [];
  // Positions outside the areas stand directly below the decision makers (the coordination, area "company").
  const top = positions.filter((position) => !areas[position.area]);
  // Organigram-First (IDD D-HP-ADMIN-042, D-VCORE-268): the executive layer over the business units, each unit a column
  // with its lead, its mission, its thread prefix, its standing and the colleagues that support it; the one truth is
  // Veridion Core's organisation. Without units (an older Core) the areas are drawn as before.
  const units = (organisation.units || []).filter((unit) => unit.id !== organisation.executive);
  const byKey = Object.fromEntries(positions.map((position) => [position.key, position]));
  const executive = positions.filter((position) => position.unit && position.unit === organisation.executive);
  const organigram = units.length > 0;
  const webexNames = Object.fromEntries((webexBots || []).filter((bot) => bot.displayName).map((bot) => [bot.key, bot.displayName]));
  const node = (position) => <PositionNode key={position.key} position={position} o={o} statusOf={statusOf} webexName={webexNames[position.key]} onWrite={onWrite} onCall={onCall} voices={voices} onVoice={onVoice} onStanding={onStanding} />;
  const designed = (definitions || []).filter((definition) => !definition.archived);
  const homes = (name) => positions.filter((position) => position.space && position.space === name)
    .map((position) => areas[position.area] || position.title);
  const showHomes = positions.some((position) => position.space);
  const showPurposes = spaces.some((space) => space.purpose);
  const order = Object.fromEntries(positions.map((position, index) => [position.key, index]));
  const membersOf = (space) => [...(space.bots || [])]
    .sort((a, b) => (order[a] ?? 99) - (order[b] ?? 99))
    .map(botOf);
  const mcpState = (status) => ({
    connected: t.admin.veridionMcpConnected,
    unreachable: t.admin.veridionMcpUnreachable,
    disabled: t.admin.veridionMcpDisabled,
  }[status] || status || '-');

  return (
    <div className="org-chart">
      {/* The human decision makers as a group, without names or addresses; the editable list stays in its own
          section below. The space in which they act comes from Veridion Core. */}
      <div className="org-chart__tier">
        <div className="org-node org-node--people">
          <div className="org-chart__label">{o.chartPeople}</div>
          <div className="org-node__text">{String(o.chartPeopleText || '').replace('{space}', organisation.humanSpace || '-')}</div>
        </div>
      </div>

      {organigram ? (
        <>
          <div className="org-chart__link" />
          <div className="org-chart__exec">
            <div className="org-chart__label">{o.executiveTitle}</div>
            <div className="org-chart__tier org-chart__tier--exec">{executive.map(node)}</div>
          </div>
          <div className="org-chart__link" />
          <div className="org-chart__label org-chart__label--units">{o.unitsTitle}</div>
          <div className="org-chart__areas org-chart__units">
            {units.map((unit) => {
              const leads = positions.filter((position) => position.unit === unit.id);
              const supporting = (unit.members || []).filter((key) => !leads.some((position) => position.key === key)).map((key) => byKey[key]).filter(Boolean);
              const standby = unit.status === 'standby';
              return (
                <div key={unit.id} className={`org-chart__area org-chart__unit${standby ? ' org-chart__unit--standby' : ''}`}>
                  <div className="org-chart__area-name">
                    <span className="org-chart__area-title">{unit.name}</span>
                    <span className="org-chart__area-badges">
                      {unit.prefix ? <span className="org-badge org-badge--prefix">{unit.prefix}</span> : null}
                      <span className={`org-badge ${standby ? 'org-badge--standby' : 'org-badge--active'}`}>{standby ? o.standingStandby : o.standingActive}</span>
                    </span>
                  </div>
                  {unit.mission ? <div className="org-node__text org-chart__mission">{unit.mission}</div> : null}
                  <div className="org-chart__area-nodes">{leads.map(node)}</div>
                  <div className="org-chart__members">
                    {supporting.length ? <div className="org-chart__members-label">{o.unitSupporting}</div> : null}
                    {supporting.map((position) => <PositionNode key={position.key} position={position} o={o} statusOf={statusOf} webexName={webexNames[position.key]} compact />)}
                  </div>
                  {onUnitAutopilot ? (
                    <button type="button" className={`${unit.autopilot ? 'homepage-button-primary' : 'homepage-button-secondary'} org-chart__switch`}
                      onClick={() => onUnitAutopilot(unit.id, !unit.autopilot)}>
                      {unit.autopilot ? t.admin.core?.autopilotUnitOn : t.admin.core?.autopilotUnitOff}
                    </button>
                  ) : null}
                  {onUnitStatus ? (
                    <button type="button" className="homepage-button-secondary org-chart__switch"
                      onClick={() => window.confirm(String(standby ? o.confirmActivate : o.confirmStandby).replace('{name}', unit.name)) && onUnitStatus(unit.id, standby ? 'active' : 'standby')}>
                      {standby ? o.standbyOff : o.standbyOn}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
          <p className="text-xs text-text-secondary org-chart__hint">{o.standbyHint}</p>
        </>
      ) : null}

      {!organigram && top.length ? (
        <>
          <div className="org-chart__link" />
          <div className="org-chart__tier">{top.map(node)}</div>
        </>
      ) : null}

      {!organigram ? (
      <>
      <div className="org-chart__link" />
      <div className="org-chart__areas">
        {Object.keys(areas).map((area) => {
          const members = positions.filter((position) => position.area === area);
          const span = Math.max(1, Math.ceil(members.length / 2));
          return (
            <div key={area} className="org-chart__area" style={{ gridColumn: `span ${span}` }}>
              <div className="org-chart__area-name">{areas[area]}</div>
              <div className="org-chart__area-nodes" style={{ gridTemplateColumns: `repeat(${span}, minmax(0, 1fr))` }}>
                {members.map(node)}
              </div>
            </div>
          );
        })}
      </div>
      </>
      ) : null}

      <div className="org-chart__band">
        <div className="org-chart__band-name">{o.chartAgentsTitle}</div>
        <div className="org-chart__band-nodes">
          {(hosted || []).map((entry) => (
            <div key={entry.id} className="org-node org-node--agent">
              <div className="org-node__name">{entry.title}</div>
              {entry.onBehalfOf?.length ? <div className="org-node__title">{o.chartFor}: {entry.onBehalfOf.map(botOf).join(', ')}</div> : null}
              {entry.description ? <div className="org-node__text">{entry.description}</div> : null}
              <div className="org-node__meta">
                <span className={entry.status === 'running' ? '' : 'org-node__marker'}>{statusOf(entry.status)}</span>
                <span>{t.admin.core.tokens}: {formatInteger(entry.used_tokens)}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="org-node org-node--agent">
          <div className="org-node__name">{o.chartDesigned}</div>
          {designed.length ? (
            <ul className="org-node__list">
              {designed.map((definition) => (
                <li key={definition.id}>
                  <span className="org-node__item">{definition.title}</span>
                  <span className="org-node__title"> · {o.chartFor}: {String(definition.origin || '').startsWith('position:') ? botOf(String(definition.origin).slice(9)) : t.admin.core.designByPerson}
                    {Number(definition.active) ? ` · ${t.admin.core.activeAgents}: ${formatInteger(definition.active)}` : ''}</span>
                </li>
              ))}
            </ul>
          ) : <div className="org-node__text">{o.none}</div>}
        </div>
      </div>

      {servers?.length ? (
        <div className="org-chart__band org-chart__band--plain">
          <div className="org-chart__band-name">{o.chartSystemsTitle}</div>
          <ul className="org-chart__systems">
            {servers.map((server) => (
              <li key={server.serverId}>
                <span className="org-node__item">{server.displayName || server.serverId}</span>
                <span className="org-node__title"> · <span className={server.status === 'connected' ? '' : 'org-node__marker'}>{mcpState(server.status)}</span></span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="org-chart__band org-chart__band--plain">
        <div className="org-chart__band-name">{o.chartRoomsTitle}</div>
        {spaces.length ? (
          <table className="org-chart__spaces">
            <thead>
              <tr>
                <th>{o.room}</th>
                {showPurposes ? <th>{o.chartSpacePurpose}</th> : null}
                {showHomes ? <th>{o.chartSpaceHome}</th> : null}
                <th>{o.roomBots}</th>
              </tr>
            </thead>
            <tbody>
              {spaces.map((space) => (
                <tr key={space.roomId || space.name}>
                  <td className="org-chart__space-name">{space.name}</td>
                  {showPurposes ? <td>{space.purpose || '-'}</td> : null}
                  {showHomes ? <td>{homes(space.name).join(', ') || '-'}</td> : null}
                  <td>{membersOf(space).join(', ') || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="org-node__text">{o.none}</div>}
      </div>
    </div>
  );
};

// -- one position in the table, with its description and interest profile -------------------------------

const SAMPLE_BYTES = 25 * 1024 * 1024;

// A file as base64, read in the browser; nothing of it goes anywhere but the admin API (IDD D-HP-ADMIN-035).
const readBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

const Position = ({ o, position, statusOf, onChanged, onMailbox, mailboxLabel, voices = [], clones = false }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(position.description);
  const [interests, setInterests] = useState(position.interests || '');
  const [threshold, setThreshold] = useState(position.threshold || 4);
  const [voice, setVoice] = useState(position.voice || '');
  const [sampleName, setSampleName] = useState(position.key);
  const [sampleFile, setSampleFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState('');
  const upload = async () => {
    if (!sampleFile || !consent) return;
    if (sampleFile.size > SAMPLE_BYTES) { setState(o.voiceTooLarge); return; }
    const audioBase64 = await readBase64(sampleFile);
    const result = await call(CORE, '/voices', { name: sampleName, audioBase64, fileName: sampleFile.name, consent: true, forBot: position.key });
    setSampleFile(null);
    setConsent(false);
    setState(`${o.voiceUploadDone} (${result.name}, ${Number(result.seconds || 0).toFixed(1)} s)`);
  };
  useEffect(() => { setText(position.description); }, [position.description]);
  useEffect(() => { setInterests(position.interests || ''); }, [position.interests]);
  useEffect(() => { setThreshold(position.threshold || 4); }, [position.threshold]);
  useEffect(() => { setVoice(position.voice || ''); }, [position.voice]);
  const run = async (action) => {
    setState(o.working);
    try {
      await action();
      setState(o.done);
      onChanged();
    } catch (error) {
      setState(`${o.failed} (${error?.code || error?.status || '-'})`);
    }
  };
  return (
    <>
      <tr>
        <td><strong>{position.bot}</strong></td>
        <td>{position.title}{position.unitName ? <div className="text-xs text-text-secondary">{position.unitName}{position.prefix ? ` · ${position.prefix}` : ''}{position.runtime ? ` · ${o.runtime}: ${position.runtime}` : ''}</div> : null}</td>
        <td>{statusOf(position.status)}{position.standing === 'standby' ? <div className="text-xs org-node__marker">{o.standingStandby}</div> : null}</td>
        <td>{formatInteger(position.usedTokens)}</td>
        <td>{formatInteger(position.tokens30Days)}{position.cost30Days ? ` · ${formatUsd(position.cost30Days)}` : ''}</td>
        <td className="admin-inline-actions">
          <button type="button" className="homepage-button-secondary" onClick={() => setOpen(!open)}>{open ? o.close : o.edit}</button>
          {onMailbox ? <button type="button" className="homepage-button-secondary" onClick={() => onMailbox(position.agentId)}>{mailboxLabel}</button> : null}
        </td>
      </tr>
      {open ? (
        <tr>
          <td colSpan="6">
            <div className="space-y-2">
              <label className="block text-sm font-bold" htmlFor={`desc-${position.key}`}>{o.description}</label>
              <textarea id={`desc-${position.key}`} className="form-input w-full" rows="5" value={text} onChange={(event) => setText(event.target.value)} />
              <div className="flex flex-wrap gap-2">
                <button type="button" className="homepage-button-secondary" onClick={() => run(() => call(CORE, `/organisation/positions/${position.key}/description`, { text }))}>{o.save}</button>
                <button type="button" className="homepage-button-secondary" onClick={() => run(() => call(CORE, `/organisation/positions/${position.key}/description`, { text: '' }))}>{o.resetDescription}</button>
              </div>
              <form className="space-y-2" onSubmit={(event) => { event.preventDefault(); run(() => call(CORE, `/organisation/positions/${position.key}/profile`, { interests, threshold: Number(threshold) })); }}>
                <label className="block text-sm font-bold" htmlFor={`interests-${position.key}`}>{o.interests}</label>
                <p className="text-xs text-text-secondary">{o.interestsHint}</p>
                <textarea id={`interests-${position.key}`} className="form-input w-full" rows="4" value={interests} onChange={(event) => setInterests(event.target.value)} />
                <div className="flex flex-wrap gap-2 items-center">
                  <label className="text-sm font-bold" htmlFor={`threshold-${position.key}`}>{o.threshold}</label>
                  <select id={`threshold-${position.key}`} className="form-input" value={threshold} onChange={(event) => setThreshold(event.target.value)}>
                    {[1, 2, 3, 4, 5].map((level) => <option key={level} value={level}>{level}</option>)}
                  </select>
                  <button type="submit" className="homepage-button-secondary">{o.saveProfile}</button>
                </div>
                <p className="text-xs text-text-secondary">{o.thresholdHint}</p>
              </form>
              {voices.length ? (
                <form className="space-y-2" onSubmit={(event) => { event.preventDefault(); run(() => call(CORE, `/organisation/positions/${position.key}/voice`, { voice })); }}>
                  <div className="flex flex-wrap gap-2 items-center">
                    <label className="text-sm font-bold" htmlFor={`voice-${position.key}`}>{o.voice}</label>
                    <select id={`voice-${position.key}`} className="form-input" value={voice} onChange={(event) => setVoice(event.target.value)}>
                      <VoiceOptions o={o} voices={voices} position={position} current={voice} />
                    </select>
                    <button type="submit" className="homepage-button-secondary">{o.voiceSave}</button>
                  </div>
                  <p className="text-xs text-text-secondary">{o.voiceHint}</p>
                </form>
              ) : null}
              {clones ? (
                <form className="space-y-2" onSubmit={(event) => { event.preventDefault(); run(upload); }}>
                  <label className="block text-sm font-bold" htmlFor={`sample-${position.key}`}>{o.voiceUploadTitle}</label>
                  <p className="text-xs text-text-secondary">{o.voiceUploadHint}</p>
                  <div className="flex flex-wrap gap-2 items-center">
                    <input id={`sample-${position.key}`} className="form-input" type="file" accept="audio/*,video/*" onChange={(event) => setSampleFile(event.target.files?.[0] || null)} />
                    <input className="form-input" type="text" value={sampleName} onChange={(event) => setSampleName(event.target.value)} placeholder={o.voiceUploadName} aria-label={o.voiceUploadName} required />
                  </div>
                  <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />{o.voiceUploadConsent}</label>
                  <button type="submit" className="homepage-button-secondary" disabled={!sampleFile || !consent}>{o.voiceUploadButton}</button>
                </form>
              ) : null}
              <p className="text-xs text-text-secondary">{o.tools}: {(position.tools || []).join(', ')}</p>
              {state ? <p className="text-sm text-text-secondary">{state}</p> : null}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
};

// -- the panel -------------------------------------------------------------------------------------------

// `hosted` are the agents Veridion Core hosts (its overview), `webexBots` the bots as the Webex bridge
// reports them and `servers` the MCP servers of the company instance; the page passes them from the one
// overview it reads anyway. `editor` is the section that edits the organisation's units and members, rendered
// by Veridion Core's panel and shown here on its own sub-page.
const VeridionOrganisationPanel = ({ t, section = 'chart', renderAgent = null, hosted = [], webexBots = [], servers = [], editor = null }) => {
  const o = t.admin.org;
  const c = t.admin.core || {};
  const { currentLang } = useTranslation();
  const sub = SUB_PAGES.includes(section) ? section : 'chart';
  const [organisation, setOrganisation] = useState(null);
  const [definitions, setDefinitions] = useState([]);
  const [reader, setReader] = useState(null);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [notice, setNotice] = useState('');
  const [mailbox, setMailbox] = useState('');
  const [writing, setWriting] = useState('');
  const [calling, setCalling] = useState('');

  const load = useCallback(async () => {
    try {
      setOrganisation(await call(CORE, '/organisation'));
      setError('');
    } catch (failure) {
      setError(failure?.code || failure?.status || 'unavailable');
    }
    try {
      setReader(await call(BRIDGE, '/reader'));
    } catch (failure) {
      setReader({ error: failure?.code || failure?.status || 'unavailable' });
    }
    try {
      setDefinitions((await call(CORE, '/definitions')).items || []);
    } catch {
      // The chart keeps the last list of definitions.
    }
  }, []);

  useEffect(() => { load(); const timer = setInterval(load, REFRESH_MS); return () => clearInterval(timer); }, [load]);

  const act = async (action, done) => {
    setNotice(o.working);
    try {
      const result = await action();
      setNotice(done || o.done);
      load();
      return result;
    } catch (failure) {
      setNotice(`${o.failed} (${failure?.code || failure?.status || '-'})`);
      return null;
    }
  };

  const authorise = async () => {
    const result = await act(() => call(BRIDGE, '/reader-link', {}), o.linkOpened);
    if (result?.url) window.open(result.url, '_blank', 'noopener');
  };

  if (error && !organisation) return <Section id="org-chart" title={o.chartTitle}><div className="homepage-muted-panel p-4 text-sm text-error">{o.failed} ({error})</div></Section>;
  if (!organisation) return <Section id="org-chart" title={o.chartTitle}><div className="homepage-muted-panel p-4 text-sm">{o.loading}</div></Section>;
  const status = reader?.status || {};
  const byKey = Object.fromEntries((organisation.positions || []).map((position) => [position.key, position]));
  const byAgent = Object.fromEntries((organisation.positions || []).map((position) => [position.agentId, position]));
  const botOf = (key) => byKey[key]?.bot || key;
  const statusOf = (value) => t.admin.core?.status?.[value] || o.status?.[value] || value || '-';
  const decisionMakerName = (maker) => {
    const name = organisation.decisionMakerNames?.[maker];
    return name ? `${name} (${maker})` : maker;
  };
  const spaces = (organisation.spaces || []).length
    ? organisation.spaces
    : (reader?.spaces || []).filter((space) => space.found);
  const approvals = organisation.approvals || [];
  const schedules = organisation.schedules || [];
  const voiceStore = organisation.voiceStore || [];
  const readerState = reader?.error ? o.failed : !status.configured ? o.notConfigured : status.authorised ? o.authorised : o.notAuthorised;
  const readerTone = reader?.error || (status.configured && !status.authorised) ? 'warning' : status.authorised ? 'ok' : '';
  // The pen opens the written conversation, the handset the call: each a dialog over the page (IDD D-HP-ADMIN-041).
  const writeTo = (agentId) => { setCalling(''); setWriting(byAgent[agentId]?.key || ''); };
  const callTo = (key) => { setWriting(''); setCalling(key); };

  return (
    <>
      {sub === 'chart' ? (
        <Section id="org-chart" title={o.chartTitle} subtitle={o.chartSubtitle} className="org-chart-card">
          <OrganisationChart o={o} t={t} organisation={organisation} hosted={hosted} definitions={definitions}
            webexBots={webexBots} servers={servers} spaces={spaces} botOf={botOf} statusOf={statusOf}
            onWrite={writeTo} onCall={callTo}
            voices={organisation.voices || []} onVoice={(key, name) => act(() => call(CORE, `/organisation/positions/${key}/voice`, { voice: name }))}
            onStanding={(key, status) => act(() => call(CORE, `/organisation/positions/${encodeURIComponent(key)}/status`, { status }))}
            onUnitStatus={(unitId, status) => act(() => call(CORE, `/organisation/units/${encodeURIComponent(unitId)}/status`, { status }))}
            onUnitAutopilot={(unitId, on) => act(() => call(CORE, '/autopilot', { unit: unitId, on }))} />
        </Section>
      ) : null}

      {sub === 'colleagues' ? (
        <Section id="org-colleagues" title={o.positionsTitle} subtitle={o.positionsSubtitle} count={(organisation.positions || []).length}>
          <div className="admin-table-shell"><div className="admin-table-scroll">
            <table className="admin-table">
              <thead><tr><th>{o.bot}</th><th>{o.position}</th><th>{o.statusLabel}</th><th>{o.tokensUsed}</th><th>{o.tokens30}</th><th /></tr></thead>
              <tbody>
                {organisation.positions.map((position) => (
                  <Position key={position.key} o={o} position={position} statusOf={statusOf} onChanged={load}
                    onMailbox={renderAgent ? setMailbox : null} mailboxLabel={t.admin.ops?.openMailbox} voices={organisation.voices || []}
                    clones={Boolean(organisation.voiceEngineClones)} />
                ))}
              </tbody>
            </table>
          </div></div>
          {mailbox && renderAgent ? renderAgent(mailbox, () => setMailbox('')) : null}
        </Section>
      ) : null}

      {sub === 'voices' ? (
        <Section id="org-voices" title={o.voicesTitle} subtitle={o.voicesSubtitle} count={organisation.voiceEngineClones ? voiceStore.length : null}>
          {organisation.voiceEngineClones ? (
            <>
              <div className="admin-table-shell"><div className="admin-table-scroll">
                <table className="admin-table">
                  <thead><tr><th>{o.voice}</th><th>{o.voiceSeconds}</th><th>{o.voiceSource}</th><th>{o.voiceWords}</th><th>{o.voiceAddedBy}</th><th>{o.voiceConsentBy}</th><th /></tr></thead>
                  <tbody>
                    {voiceStore.map((entry) => (
                      <tr key={entry.name}>
                        <td><strong>{entry.name}</strong></td>
                        <td>{Number(entry.seconds || 0).toFixed(1)}</td>
                        <td>{entry.source === 'preset' ? o.voiceSourcePreset : o.voiceSourceUpload}</td>
                        <td className="text-xs">{String(entry.words || '').slice(0, 80)}</td>
                        <td className="text-xs">{entry.addedBy || '-'} · {formatTime(entry.addedAt)}</td>
                        <td className="text-xs">{entry.consentBy || '-'}</td>
                        <td><button type="button" className="homepage-button-secondary is-destructive" onClick={() => window.confirm(o.voiceConfirmDelete) && act(() => call(CORE, `/voices/${encodeURIComponent(entry.name)}/delete`, {}))}>{o.voiceDelete}</button></td>
                      </tr>
                    ))}
                    {!voiceStore.length ? <tr><td colSpan="7">{o.none}</td></tr> : null}
                  </tbody>
                </table>
              </div></div>
              <div className="flex flex-wrap gap-2 items-center">
                <button type="button" className="homepage-button-secondary" onClick={() => act(async () => {
                  const result = await call(CORE, '/voices/presets/capture', {});
                  setNotice(`${o.voiceCaptured}: ${(result.captured || []).length}${(result.failed || []).length ? ` · ${o.failed}: ${result.failed.map((item) => item.name).join(', ')}` : ''}`);
                  return result;
                })}>{o.voiceCapture}</button>
                <span className="text-xs text-text-secondary">{o.voiceCaptureHint}</span>
              </div>
            </>
          ) : <p className="text-sm text-text-secondary">{o.voiceEnginePresets}</p>}
        </Section>
      ) : null}

      {sub === 'people' ? (
        <>
          <Section id="org-decision-makers" title={o.decisionMakersTitle} subtitle={o.decisionMakersSubtitle} count={organisation.decisionMakers.length}>
            <ul className="admin-rows">
              {organisation.decisionMakers.map((maker) => (
                <li key={maker} className="admin-row">
                  <span>{decisionMakerName(maker)}</span>
                  <button type="button" className="homepage-button-secondary is-destructive" onClick={() => window.confirm(o.confirmRemove) && act(() => call(CORE, '/organisation/decision-makers', { email: maker, remove: true }))}>{o.remove}</button>
                </li>
              ))}
              {!organisation.decisionMakers.length ? <li className="admin-row">{o.none}</li> : null}
            </ul>
            <form className="admin-form-row" onSubmit={(event) => { event.preventDefault(); act(() => call(CORE, '/organisation/decision-makers', { email })).then(() => setEmail('')); }}>
              <input className="form-input" type="email" placeholder={o.email} value={email} onChange={(event) => setEmail(event.target.value)} required />
              <button type="submit" className="homepage-button-secondary">{o.add}</button>
            </form>
          </Section>

          <Section id="org-reader" title={o.readerTitle} subtitle={o.readerSubtitle} state={readerState} tone={readerTone}>
            {reader?.error ? <p className="text-sm text-error">{o.failed} ({reader.error})</p> : (
              <>
                <p className="text-sm">
                  <strong>{!status.configured ? o.notConfigured : status.authorised ? o.authorised : o.notAuthorised}</strong>
                  {status.account ? ` · ${o.account}: ${status.account}` : ''}
                </p>
                {status.authorised ? <p className="text-xs text-text-secondary">{o.validUntil}: {formatTime(status.expiresAt)} · {o.renewUntil}: {formatTime(status.refreshExpiresAt)}</p> : null}
                {status.lastError ? <p className="text-xs text-error">{status.lastError}</p> : null}
                {status.configured ? <button type="button" className="homepage-button-primary" onClick={authorise}>{status.authorised ? o.authoriseAgain : o.authorise}</button> : null}
                <ul className="admin-rows">
                  {(reader?.spaces || []).map((space) => (
                    <li key={space.name} className="admin-row"><span><strong>{space.name}</strong>: {space.found ? `${o.found} · ${o.botsInSpace}: ${(space.bots || []).map(botOf).join(', ') || '-'}` : o.notFound}</span></li>
                  ))}
                </ul>
                <p className="text-xs text-text-secondary">{o.transcripts}: {reader?.transcriptsWebhook ? o.yes : o.no}</p>
              </>
            )}
          </Section>
        </>
      ) : null}

      {sub === 'approvals' ? (
        <>
          <Section id="org-approvals" title={o.approvalsTitle} subtitle={o.approvalsSubtitle} count={approvals.length} tone={approvals.length ? 'warning' : 'ok'} state={approvals.length ? '' : o.none}>
            <ul className="admin-rows">
              {approvals.map((approval) => (
                <li key={approval.id} className="admin-row admin-row--stack">
                  <strong>{approval.question}</strong>
                  <div className="text-xs text-text-secondary">{o.approvalKinds?.[approval.kind] || approval.kind || '-'} · {botOf(approval.position)} · {o.originSpace}: {approval.space_name || o.directRoom} · {approval.requested_by || '-'} · {formatTime(approval.created_at)}</div>
                </li>
              ))}
              {!approvals.length ? <li className="admin-row">{o.none}</li> : null}
            </ul>
          </Section>
          <Section id="org-schedules" title={o.schedulesTitle} subtitle={o.schedulesSubtitle} count={schedules.length} state={schedules.length ? '' : o.none}>
            <ul className="admin-rows">
              {schedules.map((schedule) => (
                <li key={schedule.id} className="admin-row">
                  <div>
                    <strong>{schedule.cron}</strong> · {botOf(schedule.position)} · {schedule.space_name || schedule.to_person || '-'}
                    <div className="text-xs text-text-secondary">{schedule.instruction} · {o.createdBy} {schedule.created_by || '-'} · {o.lastRun} {formatTime(schedule.last_run_at)}</div>
                  </div>
                  <button type="button" className="homepage-button-secondary is-destructive" onClick={() => window.confirm(o.confirmEnd) && act(() => call(CORE, `/organisation/schedules/${schedule.id}/end`, {}))}>{o.end}</button>
                </li>
              ))}
              {!schedules.length ? <li className="admin-row">{o.none}</li> : null}
            </ul>
          </Section>
        </>
      ) : null}

      {sub === 'edit' ? editor : null}

      {notice ? <div className="homepage-muted-panel p-3 text-sm text-text-secondary">{notice}</div> : null}

      {calling && byKey[calling] ? <CallPanel key={calling} o={o} position={byKey[calling]} language={currentLang} onClose={() => setCalling('')} /> : null}
      {writing && byKey[writing] ? <WriteDialog key={writing} o={o} c={c} position={byKey[writing]} onClose={() => setWriting('')} /> : null}
    </>
  );
};

// -- the dashboard's conversations with the colleagues (IDD D-HP-ADMIN-040) --------------------------------------

// One row per AI colleague: when the dashboard last wrote to it and when it last answered, the newest exchange, and the
// pen and the handset that open the written conversation and the call as dialogs (IDD D-HP-ADMIN-041). The threads come
// from each colleague's message door; the call keeps no record of its own beyond what the colleague says in Webex.
export const VeridionConversationsPanel = ({ t }) => {
  const o = t.admin.org;
  const c = t.admin.core || {};
  const n = t.admin.nav || {};
  const { currentLang } = useTranslation();
  const [organisation, setOrganisation] = useState(null);
  const [threads, setThreads] = useState({});
  const [error, setError] = useState('');
  const [writing, setWriting] = useState('');
  const [calling, setCalling] = useState('');
  const [shown, more] = useShowMore(50);

  const load = useCallback(async () => {
    let positions = [];
    try {
      const data = await call(CORE, '/organisation');
      setOrganisation(data);
      positions = data.positions || [];
      setError('');
    } catch (failure) {
      setError(failure?.code || failure?.status || 'unavailable');
      return;
    }
    const results = await Promise.allSettled(positions.map((position) => call(CORE, `/organisation/positions/${encodeURIComponent(position.key)}/messages`)));
    setThreads(Object.fromEntries(positions.map((position, index) => [position.key, results[index].status === 'fulfilled' ? (results[index].value.items || []) : null])));
  }, []);
  useEffect(() => { load(); const timer = setInterval(load, REFRESH_MS); return () => clearInterval(timer); }, [load]);

  if (error && !organisation) return <Section id="conversations" title={n.conversationsTitle}><div className="homepage-muted-panel p-4 text-sm text-error">{o.failed} ({error})</div></Section>;
  if (!organisation) return <Section id="conversations" title={n.conversationsTitle}><div className="homepage-muted-panel p-4 text-sm">{o.loading}</div></Section>;
  const byKey = Object.fromEntries((organisation.positions || []).map((position) => [position.key, position]));
  const rows = (organisation.positions || []).map((position) => {
    const thread = threads[position.key];
    const last = Array.isArray(thread) && thread.length ? thread[thread.length - 1] : null;
    return { position, thread, last, count: Array.isArray(thread) ? thread.length : null };
  });
  const exchanges = rows.flatMap(({ position, thread }) => (Array.isArray(thread) ? thread.map((entry) => ({ ...entry, position })) : []))
    .sort((left, right) => String(right.created_at || '').localeCompare(String(left.created_at || '')));

  return (
    <>
      <Section id="conversations" title={n.conversationsTitle} subtitle={n.conversationsSubtitle} count={rows.length}>
        <div className="admin-table-shell"><div className="admin-table-scroll">
          <table className="admin-table admin-fixed-table">
            <colgroup><col style={{ width: '18%' }} /><col style={{ width: '18%' }} /><col style={{ width: '10%' }} /><col style={{ width: '16%' }} /><col style={{ width: '16%' }} /><col style={{ width: '22%' }} /></colgroup>
            <thead><tr><th>{o.bot}</th><th>{o.position}</th><th>{n.conversationCount}</th><th>{n.conversationLastWritten}</th><th>{n.conversationLastAnswered}</th><th /></tr></thead>
            <tbody>
              {rows.map(({ position, thread, last, count }) => (
                <tr key={position.key}>
                  <td><strong>{position.bot}</strong></td>
                  <td>{position.title}</td>
                  <td>{thread === null ? <span className="text-error">{o.failed}</span> : formatInteger(count)}</td>
                  <td>{formatTime(last?.created_at)}</td>
                  <td>{formatTime(last?.answered_at)}</td>
                  <td className="admin-inline-actions">
                    <button type="button" className="homepage-button-secondary" onClick={() => { setCalling(''); setWriting(position.key); }}><PenIcon /> {o.write}</button>
                    <button type="button" className="homepage-button-secondary" onClick={() => { setWriting(''); setCalling(position.key); }}><HandsetIcon /> {o.call}</button>
                  </td>
                </tr>
              ))}
              {!rows.length ? <tr><td colSpan="6">{o.none}</td></tr> : null}
            </tbody>
          </table>
        </div></div>
      </Section>

      <Section id="conversations-exchanges" title={n.exchangesTitle} subtitle={n.exchangesSubtitle} count={exchanges.length}>
        <div className="admin-table-shell"><div className="admin-table-scroll">
          <table className="admin-table admin-fixed-table">
            <colgroup><col style={{ width: '12%' }} /><col style={{ width: '14%' }} /><col style={{ width: '37%' }} /><col style={{ width: '37%' }} /></colgroup>
            <thead><tr><th>{t.admin.ops?.time}</th><th>{o.bot}</th><th>{n.conversationMessage}</th><th>{n.conversationAnswer}</th></tr></thead>
            <tbody>
              {exchanges.slice(0, shown).map((entry) => (
                <tr key={`${entry.position.key}-${entry.id}`}>
                  <td>{formatTime(entry.created_at)}</td>
                  <td>{entry.position.bot}</td>
                  <td className="break-words" style={{ whiteSpace: 'pre-wrap' }}>{entry.text || '-'}</td>
                  <td className="break-words" style={{ whiteSpace: 'pre-wrap' }}>{entry.answer || <em>{c.answerPending}</em>}</td>
                </tr>
              ))}
              {!exchanges.length ? <tr><td colSpan="4">{c.conversationEmpty}</td></tr> : null}
            </tbody>
          </table>
        </div></div>
        <ShowMore shown={shown} total={exchanges.length} onMore={more} />
      </Section>

      {calling && byKey[calling] ? <CallPanel key={calling} o={o} position={byKey[calling]} language={currentLang} onClose={() => setCalling('')} /> : null}
      {writing && byKey[writing] ? <WriteDialog key={writing} o={o} c={c} position={byKey[writing]} onClose={() => { setWriting(''); load(); }} /> : null}
    </>
  );
};

export default VeridionOrganisationPanel;
